import type { FormField, FormFieldType, FormSchema } from "@domain/form";
import { FORM_FIELD_TYPES, ONLINE_ONLY_FIELD_TYPES } from "@domain/form";

/**
 * ⚠️ Tiplarning YAGONA manbai — `@domain/form`. `FormAnswers` ham shu
 * yerdan (kontrakt §3): u Task 7 dagi `FormPreview` ning holat tipi va
 * uni frontendda qayta e'lon qilish `visibleFields()` bilan ajralishga
 * olib kelardi.
 */
export type {
	FormAnswers,
	FormCondition,
	FormField,
	FormFieldOption,
	FormFieldType,
	FormSchema,
} from "@domain/form";

export interface LeadForm {
	id: string;
	name: string;
	description: string | null;
	isActive: boolean;
	/** ISO 8601. */
	archivedAt: string | null;
	/** Versiyasiz forma uchun `null` — qoralama holati yo'q (spec §7.2). */
	latestVersion: number | null;
	createdAt: string;
}

export interface LeadFormVersionSummary {
	id: string;
	version: number;
	publishedAt: string;
	publishedBy: string | null;
}

export interface LeadFormDetail extends LeadForm {
	/** Kamayish tartibida — birinchisi eng yangisi. */
	versions: LeadFormVersionSummary[];
}

export interface LeadFormVersion extends LeadFormVersionSummary {
	formId: string;
	schema: FormSchema;
}

export type LeadFormAssignmentKind = "employee" | "department" | "position";

export interface LeadFormAssignment {
	id: string;
	kind: LeadFormAssignmentKind;
	targetId: string;
	createdAt: string;
}

export const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
	text: "Matn",
	textarea: "Uzun matn",
	number: "Raqam",
	phone: "Telefon",
	email: "E-pochta",
	date: "Sana",
	time: "Vaqt",
	datetime: "Sana va vaqt",
	select: "Ro'yxatdan tanlash",
	multiselect: "Ko'p tanlash",
	boolean: "Ha / Yo'q",
	rating: "Baho",
	file: "Fayl",
	image: "Rasm",
	signature: "Imzo",
	geopoint: "Xarita nuqtasi",
	section: "Bo'lim sarlavhasi",
};

export const FIELD_TYPE_OPTIONS = FORM_FIELD_TYPES.map((type) => ({
	value: type,
	label: FIELD_TYPE_LABELS[type],
}));

/**
 * Oflayn to'ldirib bo'lmaydigan turlar (spec §4.2, R6). Ro'yxat
 * `@domain/form` dan keladi — bu yerda faqat qidiruv uchun `Set`.
 * Konstruktor buni ogohlantirish belgisi sifatida ko'rsatadi;
 * `offlineCapable` ni SERVER hisoblaydi.
 */
export const ONLINE_ONLY_TYPES = new Set<FormFieldType>(ONLINE_ONLY_FIELD_TYPES);

export function needsOptions(type: FormFieldType): boolean {
	return type === "select" || type === "multiselect";
}

/** `section` javob bermaydi (spec §4.1) — shart manbai ham bo'lolmaydi. */
export function isAnswerable(field: FormField): boolean {
	return field.type !== "section";
}

/**
 * Yangi maydon. `key` `^[a-z][a-z0-9_]*$` shaklida bo'lishi SHART
 * (`validateFormSchema` ning `bad_key` xatosi) va forma ichida unikal —
 * shuning uchun mavjud kalitlar berilib, birinchi bo'sh raqam olinadi.
 */
export function createField(type: FormFieldType, existingKeys: string[]): FormField {
	const taken = new Set(existingKeys);
	let index = existingKeys.length + 1;

	while (taken.has(`maydon_${index}`)) {
		index += 1;
	}

	const field: FormField = {
		key: `maydon_${index}`,
		type,
		label: FIELD_TYPE_LABELS[type],
		required: false,
	};

	return needsOptions(type)
		? { ...field, options: [{ value: "variant_1", label: "Variant 1" }] }
		: field;
}
